import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { BriefFieldDefinition, BriefData } from '../types/briefTypes';
import { AIPromptConfig } from '../types/promptTypes';
import { ConversationConfig } from '../types/conversationTypes';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

interface TemplateData {
  fields: BriefFieldDefinition[];
  aiPrompts: AIPromptConfig;
  conversationConfig: ConversationConfig;
}

export const saveTemplate = async (userId: string, data: TemplateData): Promise<boolean> => {
  try {
    const templateRef = doc(db, 'templates', userId);
    await setDoc(templateRef, {
      fields: data.fields,
      aiPrompts: data.aiPrompts,
      conversationConfig: data.conversationConfig,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error saving template:', error);
    return false;
  }
};

export const loadTemplate = async (userId: string): Promise<TemplateData | null> => {
  try {
    const templateRef = doc(db, 'templates', userId);
    const templateDoc = await getDoc(templateRef);
    
    if (templateDoc.exists()) {
      const data = templateDoc.data() as TemplateData;
      return {
        fields: data.fields,
        aiPrompts: data.aiPrompts,
        conversationConfig: {
          ...data.conversationConfig,
          prerequisites: data.conversationConfig.prerequisites || []
        }
      };
    }
    return null;
  } catch (error) {
    console.error('Error loading template:', error);
    return null;
  }
};

export const saveBrief = async (userId: string, briefId: string, briefData: BriefData) => {
  try {
    const briefRef = doc(db, 'users', userId, 'briefs', briefId);
    await setDoc(briefRef, briefData);
    return true;
  } catch (error) {
    console.error('Error saving brief:', error);
    return false;
  }
};

export const updateBrief = async (userId: string, briefId: string, briefData: Partial<BriefData>) => {
  try {
    const briefRef = doc(db, 'users', userId, 'briefs', briefId);
    await setDoc(briefRef, briefData, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating brief:', error);
    return false;
  }
};

export const getBrief = async (userId: string, briefId: string): Promise<BriefData | null> => {
  try {
    const briefRef = doc(db, 'users', userId, 'briefs', briefId);
    const briefSnap = await getDoc(briefRef);
    
    if (briefSnap.exists()) {
      return briefSnap.data() as BriefData;
    }
    return null;
  } catch (error) {
    console.error('Error getting brief:', error);
    return null;
  }
}; 